import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useSearchParams } from 'react-router-dom';
import { DetailDrawer } from '../components/common/DetailDrawer';
import { Modal } from '../components/common/Modal';
import { shippingApi } from '../api/shippingApi';
import { buildPeriodApiFilters, fiscalPeriodInfo } from '../utils/fiscalPeriod';
import { resetFiltersToDefault } from '../utils/filterReset';
import {
  SHIPPING_ADJUST_DEFAULT_FILTERS,
  buildShippingAdjustFiltersFromSearchParams,
} from '../constants/shippingAdjustFilters';
import { HacchuSplitPlanInlineEditor } from '../features/shipping/HacchuSplitPlanInlineEditor';
import { patchItemsSplitPlans } from '../features/shipping/splitPlanUtils';
import { PurchaseOrderModal } from '../features/shipping/PurchaseOrderModal';
import { ShipmentMemoBadges } from '../features/shipping/ShipmentMemoBadges';
import {
  getShipmentDateUnsetCellClassName,
  isShipmentDateUnset,
} from '../features/shipping/shipmentDateUtils';
import { ShipmentPlanDetailFields } from '../features/shipping/ShipmentPlanDetailFields';
import { formatShipmentPlanDate } from '../features/shipping/shipmentPlanFieldUtils';
import { useShipmentPlanFieldSave } from '../features/shipping/useShipmentPlanFieldSave';
import { PageSyncMeta } from '../components/sync/PageSyncMeta';
import { useSyncStatus } from '../contexts/SyncStatusContext';
import {
  getShipmentRowClassName,
  getShipmentRowStage,
  getShipmentRowStickyCellClassName,
} from '../utils/getShipmentRowStage';
import {
  PRINT_PAGE_TITLE_COUNT,
  PRINT_PAGE_TITLE_H1,
  PRINT_PAGE_TITLE_ROW,
  PRINT_SCROLL_CONTAINER,
  PRINT_SEARCH_ROW_TH,
  PRINT_SEARCH_ROW_TR,
  PRINT_TABLE,
  PRINT_TD_COMPOSITE,
  PRINT_TD_DEFAULT_LEFT,
  PRINT_TD_DEFAULT_NUM,
  PRINT_TD_LEFT,
  PRINT_TD_NUM,
  PRINT_THEAD,
  PRINT_TH_LEFT,
  PRINT_TH_RIGHT,
  PRINT_TR_BODY,
} from '../constants/printTableStyles';

const ShippingAdjustPage = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const [items, setItems] = useState([]);
  const [selectedItem, setSelectedItem] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadingMore, setLoadingMore] = useState(false);
  const [hasMore, setHasMore] = useState(true);
  const [expandedRows, setExpandedRows] = useState(new Set()); // 展開されている行のIDを保持
  const [purchaseOrderModalOpen, setPurchaseOrderModalOpen] = useState(false);
  const [selectedHacchuNo, setSelectedHacchuNo] = useState(null);
  const [shipmentDestinationTotalWeight, setShipmentDestinationTotalWeight] = useState(null); // 発送先総重量
  const [loadingTotalWeight, setLoadingTotalWeight] = useState(false); // 発送先総重量の読み込み中フラグ
  const [periodWarningOpen, setPeriodWarningOpen] = useState(false);
  const [pendingFiltersForPeriodWarning, setPendingFiltersForPeriodWarning] = useState(null);
  const offsetRef = useRef(0);
  const inputRefs = useRef({});
  const observerTarget = useRef(null);
  const observerRef = useRef(null);
  const loadingInitialRef = useRef(false); // 初期ロード実行中フラグ
  const listRequestVersionRef = useRef(0); // 一覧リクエストの世代管理（古いレスポンス破棄用）

  const fiscalMeta = useMemo(() => fiscalPeriodInfo(), []);

  // 得意先コード表示用：基幹どおり（先頭0を保持して）表示する
  const formatCustomerDisp = (value) => {
    if (value === null || value === undefined || value === '') return '-';
    return String(value).trim();
  };
  
  // フィルタ状態（URLパラメータから初期化）
  // filters: 入力中のフィルタ値（検索ボタン押下まで適用されない）
  // appliedFilters: 実際に適用されているフィルタ値
  const initialFilters = buildShippingAdjustFiltersFromSearchParams(searchParams);
  const [filters, setFilters] = useState(initialFilters);
  const [appliedFilters, setAppliedFilters] = useState(initialFilters);
  const { refreshSyncStatus } = useSyncStatus();
  // 印刷用ビュー（print_view）の表列のみに使用
  const PRINT_PRESET_FIELDS = [
    'latest_shipment_date',
    'arrival_date',
    'nonyusaki_direct_address',
    'product_disp',
    'product_name_size',
    'hacchu_amount',
    'pl_qty',
    'package_qty',
    'transport_service',
    'product_weight',
    'total_weight',
    'shipment_destination_total_weight',
    'customer_code_for_print',
    'juchu_hacchu_no',
    'tracking_number',
    'comment',
  ];
  const MANDATORY_PRINT_FIELDS = ['latest_shipment_date', 'nonyusaki_direct_address'];
  // 印刷用ビュー関連ステート
  const [printItems, setPrintItems] = useState([]);
  const [printLoading, setPrintLoading] = useState(false);
  const [printError, setPrintError] = useState(null);
  const [printTotalCount, setPrintTotalCount] = useState(null);
  const [printSelectedFields, setPrintSelectedFields] = useState(() => [...PRINT_PRESET_FIELDS]);
  const PRINT_COLUMN_WIDTHS = {
    latest_shipment_date: '6%',
    arrival_date: '6%',
    nonyusaki_direct_address: '18%',
    product_disp: '4%',
    product_name_size: '16%',
    hacchu_amount: '5%',
    pl_qty: '3%',
    package_qty: '3%',
    transport_service: '4%',
    product_weight: '3%',
    total_weight: '3%',
    shipment_destination_total_weight: '4%',
    customer_code_for_print: '4%',
    juchu_hacchu_no: '7%',
    tracking_number: '5%',
    comment: '4%',
  };
  const PRINT_RIGHT_ALIGN_FIELDS = new Set([
    'hacchu_amount',
    'pl_qty',
    'package_qty',
    'product_weight',
    'total_weight',
    'shipment_destination_total_weight',
  ]);

  // 最新の状態を参照するためのref
  const stateRef = useRef({ loadingMore: false, hasMore: true, loading: true });
  const appliedFiltersRef = useRef(appliedFilters);
  const isPrintView = searchParams.get('print_view') === '1';
  
  useEffect(() => {
    stateRef.current = { loadingMore, hasMore, loading };
  }, [loadingMore, hasMore, loading]);

  useEffect(() => {
    appliedFiltersRef.current = appliedFilters;
  }, [appliedFilters]);

  // 印刷用ビュー表示時は body にクラスを付け、印刷時に親のスクロール領域を解放する
  useEffect(() => {
    if (isPrintView) {
      document.body.classList.add('shipping-print-view');
    }
    return () => {
      document.body.classList.remove('shipping-print-view');
    };
  }, [isPrintView]);

  // URLパラメータを更新
  const updateSearchParams = (newFilters) => {
    const params = new URLSearchParams();
    if (newFilters.productQuery) {
      params.set('product_query', newFilters.productQuery);
    }
    if (newFilters.customerName) {
      params.set('customer_name', newFilters.customerName);
    }
    if (newFilters.customerNameExact) {
      params.set('customer_name_exact', 'true');
    }
    if (newFilters.startDate) {
      params.set('start_date', newFilters.startDate);
    }
    if (newFilters.endDate) {
      params.set('end_date', newFilters.endDate);
    }
    // 検索基準日（デフォルトは発注日なので、発送日が選択されている場合のみ追加）
    if (newFilters.searchBaseDate && newFilters.searchBaseDate !== 'order_date') {
      params.set('search_base_date', newFilters.searchBaseDate);
    }
    // shipmentDateNullOnlyがtrueの場合のみURLパラメータに追加
    // falseの場合は追加しない（既存のパラメータがあれば削除される）
    if (newFilters.shipmentDateNullOnly) {
      params.set('shipment_date_null_only', 'true');
    }
    // 発注日起点のフィルタ
    if (newFilters.hacchuDateFromToday) {
      params.set('hacchu_date_from_today', 'true');
    }
    // 発送日起点のフィルタ
    if (newFilters.shipmentDateTodayOnly) {
      params.set('shipment_date_today_only', 'true');
    }
    if (newFilters.shipmentDateFromToday) {
      params.set('shipment_date_from_today', 'true');
    }
    if (newFilters.shipmentDateNextBusinessDay) {
      params.set('shipment_date_next_business_day', 'true');
    }
    if (newFilters.shipmentDateFromNextBusinessDay) {
      params.set('shipment_date_from_next_business_day', 'true');
    }
    // 期のフィルタ
    if (newFilters.currentPeriodOnly) {
      params.set('current_period_only', 'true');
    } else if (newFilters.previousPeriodsOnly) {
      params.set('previous_periods_only', 'true');
    }
    if (newFilters.defectiveOnly) {
      params.set('cancel_return_issue_only', 'true');
    }
    // 両方falseの場合はパラメータに含めない（全期間表示）
    // 注意: falseの場合はparamsに追加しないため、既存のパラメータは自動的に削除される
    setSearchParams(params, { replace: true });
  };

  // 発送日候補を取得するヘルパー関数
  // 分納計画に発送日がある場合はその全件を対象にし、
  // 分納に日付がない場合のみ明細レベルの発送日をフォールバックとして使用する。
  const getShipmentDates = (item) => {
    const normalizeDate = (value) => {
      if (!value) return null;
      const d = new Date(value);
      if (Number.isNaN(d.getTime())) return null;
      d.setHours(0, 0, 0, 0);
      return d;
    };

    if (item.split_plans && item.split_plans.length > 0) {
      const splitDates = item.split_plans
        .map(plan => normalizeDate(plan.shipment_date))
        .filter(Boolean);
      if (splitDates.length > 0) {
        return splitDates;
      }
    }

    const fallbackDate = normalizeDate(item.latest_shipment_date || item.shipment_date);
    return fallbackDate ? [fallbackDate] : [];
  };

  // ソート用の代表発送日（最小日付）
  const getPrimaryShipmentDate = (item) => {
    const dates = getShipmentDates(item);
    if (dates.length === 0) return null;
    return new Date(Math.min(...dates.map(d => d.getTime())));
  };

  // 翌営業日を計算するヘルパー関数（土日を除く）
  const getNextBusinessDay = (date) => {
    const nextDay = new Date(date);
    nextDay.setDate(nextDay.getDate() + 1);
    const dayOfWeek = nextDay.getDay();
    // 土曜日(6)の場合は月曜日(1)に、日曜日(0)の場合は月曜日(1)に
    if (dayOfWeek === 0) {
      nextDay.setDate(nextDay.getDate() + 1); // 日曜日→月曜日
    } else if (dayOfWeek === 6) {
      nextDay.setDate(nextDay.getDate() + 2); // 土曜日→月曜日
    }
    return nextDay;
  };

  const shouldShowPeriodWarning = (f) => {
    const hasPeriodFilter = f.currentPeriodOnly || f.previousPeriodsOnly;
    const hasDateRange = f.startDate || f.endDate;
    if (!hasPeriodFilter || !hasDateRange) return false;

    const start = f.startDate || null;
    const end = f.endDate || null;

    // 今期のみ: 開始日が今期開始日より前なら警告
    if (f.currentPeriodOnly) {
      if (!start) {
        // 開始日がない場合は仕様上警告しない
        return false;
      }
      return start < fiscalMeta.period_start;
    }

    // 前期以前のみ:
    // 開始日・終了日ともに指定されていて、
    // 期間 [start, end] が今期と重なる場合に警告
    if (f.previousPeriodsOnly) {
      if (!start || !end) {
        // 「開始日・終了日ともに今期の範囲にかぶっている」条件を満たせないので警告しない
        return false;
      }

      const overlapsCurrent =
        start <= fiscalMeta.period_end &&
        end >= fiscalMeta.period_start;

      return overlapsCurrent;
    }

    return false;
  };

  const loadInitialItems = useCallback(async () => {
    // 既に実行中の場合はスキップ（React Strict Mode の二重実行を防ぐ）
    if (loadingInitialRef.current) {
      return;
    }
    
    try {
      const requestVersion = ++listRequestVersionRef.current;
      loadingInitialRef.current = true;
      setLoading(true);
      offsetRef.current = 0; // フィルター変更時はoffsetをリセット
      // 印刷用ビューでは印刷上限件数まで一括取得し、それ以外は無限スクロール用に100件ずつ取得
      const initialLimit = isPrintView ? 1000 : 100;
      const filtersWithPeriod = buildPeriodApiFilters(appliedFilters);
      // API クライアントは JSON 本体（配列）をそのまま返すため、そのまま受け取る
      let data = await shippingApi.getShipmentPlan(initialLimit, 0, filtersWithPeriod);
      if (requestVersion !== listRequestVersionRef.current) {
        return;
      }
      const originalDataLength = data.length; // フィルタリング前の件数を保持（最初に定義）
      
      // 発送日未設定の絞り込み（latest_shipment_dateのみで判定）
      if (appliedFilters.shipmentDateNullOnly) {
        data = data.filter(item => {
          return !item.latest_shipment_date;
        });
        
        // 発注日文字列をパースするヘルパー関数
        // 形式: "24/01/15(月)" または "2024-01-15" など
        const parseHacchuDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return null;
          
          // "24/01/15(月)" 形式の場合
          const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})/);
          if (match) {
            const year = 2000 + parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            return new Date(year, month, day);
          }
          
          // ISO形式やその他の形式を試す
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
          
          return null;
        };
        
        // 発注日が古い順にソート
        data.sort((a, b) => {
          const dateA = parseHacchuDate(a.hacchu_date);
          const dateB = parseHacchuDate(b.hacchu_date);
          
          // 両方nullの場合は順序を変えない
          if (!dateA && !dateB) return 0;
          // nullの場合は最後に配置
          if (!dateA) return 1;
          if (!dateB) return -1;
          
          return dateA - dateB; // 古い順（昇順）
        });
      }
      
      // 発送日「設定済」の絞り込み（latest_shipment_dateのみで判定）
      if (appliedFilters.shipmentDateSetOnly) {
        data = data.filter(item => {
          return !!item.latest_shipment_date;
        });
      }
      
      // 今日以降のフィルタリング（発注日ベース）
      if (appliedFilters.hacchuDateFromToday) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 発注日文字列をパースするヘルパー関数（発送日未設定と同じ）
        const parseHacchuDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return null;
          
          // "24/01/15(月)" 形式の場合
          const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})/);
          if (match) {
            const year = 2000 + parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            return new Date(year, month, day);
          }
          
          // ISO形式やその他の形式を試す
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
          
          return null;
        };
        
        data = data.filter(item => {
          const hacchuDate = parseHacchuDate(item.hacchu_date);
          
          if (!hacchuDate) {
            return false; // 発注日がない場合は除外
          }
          
          hacchuDate.setHours(0, 0, 0, 0);
          return hacchuDate >= today;
        });
        
        // 発注日が古い順にソート
        data.sort((a, b) => {
          const dateA = parseHacchuDate(a.hacchu_date);
          const dateB = parseHacchuDate(b.hacchu_date);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA - dateB; // 古い順（昇順）
        });
      }
      
      // 発送日起点のフィルタリング
      if (appliedFilters.shipmentDateTodayOnly ||
          appliedFilters.shipmentDateFromToday || 
          appliedFilters.shipmentDateNextBusinessDay || 
          appliedFilters.shipmentDateFromNextBusinessDay) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        let thresholdDate = today;
        if (appliedFilters.shipmentDateNextBusinessDay || appliedFilters.shipmentDateFromNextBusinessDay) {
          thresholdDate = getNextBusinessDay(today);
          thresholdDate.setHours(0, 0, 0, 0);
        }
        
        data = data.filter(item => {
          const shipmentDates = getShipmentDates(item);
          if (shipmentDates.length === 0) {
            return false; // 発送日がない場合は除外
          }

          if (appliedFilters.shipmentDateTodayOnly) {
            return shipmentDates.some(date => date.getTime() === today.getTime());
          } else if (appliedFilters.shipmentDateNextBusinessDay) {
            // 翌営業日のみ（完全一致）
            return shipmentDates.some(date => date.getTime() === thresholdDate.getTime());
          } else {
            // 今日以降または翌営業日以降
            return shipmentDates.some(date => date >= thresholdDate);
          }
        });
        
        // 発送日が古い順にソート
        data.sort((a, b) => {
          const dateA = getPrimaryShipmentDate(a);
          const dateB = getPrimaryShipmentDate(b);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA - dateB; // 古い順（昇順）
        });
      }
      
      setItems(data);
      // offsetRefはバックエンドから取得した累積件数（フィルタリング前）で更新
      // 次のloadMoreItemsで正しいoffsetを使用するため
      offsetRef.current = originalDataLength;
      // hasMoreはバックエンドから取得した件数（フィルタリング前）で判定
      setHasMore(originalDataLength === 100);
    } catch (error) {
      console.error('[ERROR] 一覧取得エラー:', error);
      alert('一覧の取得に失敗しました');
    } finally {
      loadingInitialRef.current = false;
      setLoading(false);
    }
  }, [appliedFilters]);

  const handleRefresh = async () => {
    await Promise.all([loadInitialItems(), refreshSyncStatus()]);
  };

  // 初期読み込み時とappliedFilters変更時にデータを読み込む
  useEffect(() => {
    loadInitialItems();
  }, [loadInitialItems]);

  const loadMoreItems = async () => {
    const currentState = stateRef.current;
    if (currentState.loadingMore || !currentState.hasMore || currentState.loading) {
      return;
    }

    try {
      const requestVersion = listRequestVersionRef.current;
      const currentOffset = offsetRef.current;
      const currentAppliedFilters = appliedFiltersRef.current;
      setLoadingMore(true);
      const filtersWithPeriod = buildPeriodApiFilters(currentAppliedFilters);
      let data = await shippingApi.getShipmentPlan(100, currentOffset, filtersWithPeriod);
      if (requestVersion !== listRequestVersionRef.current) {
        return;
      }
      const originalDataLength = data.length; // フィルタリング前の件数を保持
      
      // 発送日未設定の絞り込み（latest_shipment_dateのみで判定）
      if (currentAppliedFilters.shipmentDateNullOnly) {
        data = data.filter(item => {
          return !item.latest_shipment_date;
        });
      }
      
      // 発送日「設定済」の絞り込み（latest_shipment_dateのみで判定）
      if (currentAppliedFilters.shipmentDateSetOnly) {
        data = data.filter(item => {
          return !!item.latest_shipment_date;
        });
      }
      
      // 今日以降のフィルタリング（発注日ベース、loadInitialItemsと同じロジック）
      if (currentAppliedFilters.hacchuDateFromToday) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        // 発注日文字列をパースするヘルパー関数（発送日未設定と同じ）
        const parseHacchuDate = (dateStr) => {
          if (!dateStr || dateStr === '-') return null;
          
          // "24/01/15(月)" 形式の場合
          const match = dateStr.match(/^(\d{2})\/(\d{2})\/(\d{2})/);
          if (match) {
            const year = 2000 + parseInt(match[1], 10);
            const month = parseInt(match[2], 10) - 1;
            const day = parseInt(match[3], 10);
            return new Date(year, month, day);
          }
          
          // ISO形式やその他の形式を試す
          const date = new Date(dateStr);
          if (!isNaN(date.getTime())) {
            return date;
          }
          
          return null;
        };
        
        data = data.filter(item => {
          const hacchuDate = parseHacchuDate(item.hacchu_date);
          if (!hacchuDate) {
            return false; // 発注日がない場合は除外
          }
          
          hacchuDate.setHours(0, 0, 0, 0);
          return hacchuDate >= today;
        });
        
        // 発注日が古い順にソート
        data.sort((a, b) => {
          const dateA = parseHacchuDate(a.hacchu_date);
          const dateB = parseHacchuDate(b.hacchu_date);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA - dateB; // 古い順（昇順）
        });
      }
      
      // 発送日起点のフィルタリング
      if (currentAppliedFilters.shipmentDateTodayOnly ||
          currentAppliedFilters.shipmentDateFromToday || 
          currentAppliedFilters.shipmentDateNextBusinessDay || 
          currentAppliedFilters.shipmentDateFromNextBusinessDay) {
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        
        let thresholdDate = today;
        if (currentAppliedFilters.shipmentDateNextBusinessDay || currentAppliedFilters.shipmentDateFromNextBusinessDay) {
          thresholdDate = getNextBusinessDay(today);
          thresholdDate.setHours(0, 0, 0, 0);
        }
        
        data = data.filter(item => {
          const shipmentDates = getShipmentDates(item);
          if (shipmentDates.length === 0) {
            return false; // 発送日がない場合は除外
          }

          if (currentAppliedFilters.shipmentDateTodayOnly) {
            return shipmentDates.some(date => date.getTime() === today.getTime());
          } else if (currentAppliedFilters.shipmentDateNextBusinessDay) {
            // 翌営業日のみ（完全一致）
            return shipmentDates.some(date => date.getTime() === thresholdDate.getTime());
          } else {
            // 今日以降または翌営業日以降
            return shipmentDates.some(date => date >= thresholdDate);
          }
        });
        
        // 発送日が古い順にソート
        data.sort((a, b) => {
          const dateA = getPrimaryShipmentDate(a);
          const dateB = getPrimaryShipmentDate(b);
          if (!dateA && !dateB) return 0;
          if (!dateA) return 1;
          if (!dateB) return -1;
          return dateA - dateB; // 古い順（昇順）
        });
      }
      
      // フィルタリング後のデータが0件の場合、次のAPI呼び出しをスキップするために
      // offsetRefを更新して、hasMoreをfalseにする
      if (data.length === 0) {
        // フィルタリング後のデータが0件の場合でも、バックエンドから取得した件数でoffsetを更新
        offsetRef.current = currentOffset + originalDataLength;
        // バックエンドから取得した件数が100件未満の場合は、これ以上データがない
        if (originalDataLength < 100) {
          setHasMore(false);
        }
        // データが0件の場合は追加しない
      } else {
        setItems(prevItems => [...prevItems, ...data]);
        // offsetRefはフィルタリング前の件数で更新（バックエンドから取得した件数）
        offsetRef.current = currentOffset + originalDataLength;
        // hasMoreはバックエンドから取得した件数（フィルタリング前）で判定
        setHasMore(originalDataLength === 100);
      }
    } catch (error) {
      console.error('[ERROR] 追加データ取得エラー:', error);
    } finally {
      setLoadingMore(false);
    }
  };

  const handleFilterChange = (field, value) => {
    const newFilters = { ...filters, [field]: value };

    // 発送日関連ボタンは排他（常にどれか1つのみ選択可能）
    const shipmentFilterFields = [
      'shipmentDateNullOnly',
      'shipmentDateSetOnly',
      'shipmentDateTodayOnly',
      'shipmentDateFromToday',
      'shipmentDateNextBusinessDay',
      'shipmentDateFromNextBusinessDay'
    ];
    if (shipmentFilterFields.includes(field) && value) {
      shipmentFilterFields.forEach((key) => {
        if (key !== field) {
          newFilters[key] = false;
        }
      });
    }

    // 期フィルタも排他的
    if (field === 'currentPeriodOnly' && value) {
      newFilters.previousPeriodsOnly = false;
    } else if (field === 'previousPeriodsOnly' && value) {
      newFilters.currentPeriodOnly = false;
    }
    
    // ここでは filters のみ更新し、実際の適用は検索ボタン押下時に行う
    setFilters(newFilters);
  };

  const handleFilterClear = (field) => {
    const toggleFields = [
      'shipmentDateNullOnly',
      'shipmentDateSetOnly',
      'hacchuDateFromToday',
      'shipmentDateTodayOnly',
      'shipmentDateFromToday',
      'shipmentDateNextBusinessDay',
      'shipmentDateFromNextBusinessDay'
    ];
    const newFilters = { ...filters, [field]: toggleFields.includes(field) ? false : '' };
    // ここでも filters のみ更新し、検索ボタン押下時に反映する
    setFilters(newFilters);
  };

  const handleAllFiltersClear = () => {
    resetFiltersToDefault({
      defaults: SHIPPING_ADJUST_DEFAULT_FILTERS,
      setFilters,
      setAppliedFilters,
      updateSearchParams,
    });
  };

  const handleFilterSubmit = (e) => {
    e.preventDefault();
    const nextFilters = { ...filters };
    if (shouldShowPeriodWarning(nextFilters)) {
      // 危ない組み合わせの場合は、まず確認ダイアログを表示し、
      // ユーザーの選択に応じてフィルタを適用する。
      setPendingFiltersForPeriodWarning(nextFilters);
      setPeriodWarningOpen(true);
      return;
    }
    // 通常ケース: そのままフィルタを適用
    setAppliedFilters(nextFilters);
    updateSearchParams(nextFilters);
  };

  useEffect(() => {
    // items.lengthが0の場合は、observerTargetが存在しないのでスキップ
    if (items.length === 0) {
      return;
    }
    
    // オブザーバーは一度だけ作成
    if (!observerRef.current) {
      observerRef.current = new IntersectionObserver(
        (entries) => {
          const entry = entries[0];
          if (!entry) return;

          const currentState = stateRef.current;

          if (entry.isIntersecting && currentState.hasMore && !currentState.loadingMore && !currentState.loading) {
            loadMoreItems();
          }
        },
        { 
          threshold: 0,
          rootMargin: '500px'
        }
      );
    }

    const observer = observerRef.current;

    // observerTargetが存在するまで待機してからオブザーバーを設定
    const setupObserver = () => {
      const target = observerTarget.current;
      if (target) {
        // 既に監視中の場合は一度解除してから再設定（確実に監視を開始するため）
        const isObserving = target.getAttribute('data-observing') === 'true';
        if (isObserving) {
          observer.unobserve(target);
          target.removeAttribute('data-observing');
        }
        // 少し待ってから再設定（DOM更新を待つ）
        const timeoutId = setTimeout(() => {
          const currentTarget = observerTarget.current;
          if (currentTarget && !currentTarget.getAttribute('data-observing')) {
            observer.observe(currentTarget);
            currentTarget.setAttribute('data-observing', 'true');
          }
        }, 50);
        return { timeoutId };
      }
      return null;
    };

    // 即座にチェック
    let setupResult = setupObserver();
    let timeoutId1 = null;
    let timeoutId2 = null;
    let timeoutId3 = null;

    if (!setupResult) {
      // 少し待ってから再試行（DOMが更新されるのを待つ）
      timeoutId1 = setTimeout(() => {
        setupResult = setupObserver();
        if (!setupResult) {
          // もう一度試行
          timeoutId2 = setTimeout(() => {
            setupResult = setupObserver();
            if (!setupResult) {
              // 最後の試行
              timeoutId3 = setTimeout(() => {
                setupObserver();
              }, 300);
            }
          }, 300);
        }
      }, 300);
    }

    return () => {
      if (setupResult?.timeoutId) clearTimeout(setupResult.timeoutId);
      if (timeoutId1) clearTimeout(timeoutId1);
      if (timeoutId2) clearTimeout(timeoutId2);
      if (timeoutId3) clearTimeout(timeoutId3);
      const target = observerTarget.current;
      if (target && observer) {
        observer.unobserve(target);
        target.removeAttribute('data-observing');
      }
    };
  }, [hasMore, items.length]); // hasMoreとitems.lengthを依存配列に追加

  const getRowId = (row) => {
    return `${row.hacchu_code}-${row.hacchu_changecnt}-${row.hacchu_datacode}`;
  };

  const handleRowClick = (row, e) => {
    // 「詳細」ボタンのクリックは展開をトリガーしない
    if (e.target.closest('.detail-button')) {
      return;
    }
    // 発注No列のクリックは展開をトリガーしない（モーダルを開く）
    if (e.target.closest('button[class*="text-blue-600"]')) {
      return;
    }
    
    const rowId = getRowId(row);
    const newExpandedRows = new Set(expandedRows);
    
    if (newExpandedRows.has(rowId)) {
      // 既に開いている場合は閉じる
      newExpandedRows.delete(rowId);
    } else {
      // 新しい行を開く場合は、既存の展開をすべて閉じてから開く
      newExpandedRows.clear();
      newExpandedRows.add(rowId);
    }
    
    setExpandedRows(newExpandedRows);
  };

  // 発送先総重量を計算する共通関数
  const calculateShipmentDestinationTotalWeight = async (item) => {
    // デフォルトでその明細の総重量（製品の重量×発注数量）を設定
    const defaultWeight = item.total_weight || 0;
    
    // 発送日はlatest_shipment_dateを優先（分納計画がある場合は分納計画の最新発送日）
    const shipmentDate = item.latest_shipment_date || item.shipment_date;
    const nonyusaki = item.nonyusaki;
    
    // 発送日と納入先が設定されている場合、同じ発送日・納入先の他の明細を取得して加算
    if (shipmentDate && nonyusaki) {
      setLoadingTotalWeight(true);
      try {
        // 発送日をYYYY-MM-DD形式に変換
        let shipmentDateStr = null;
        if (shipmentDate instanceof Date) {
          shipmentDateStr = shipmentDate.toISOString().split('T')[0];
        } else if (typeof shipmentDate === 'string') {
          // 既にYYYY-MM-DD形式の場合はそのまま使用
          if (shipmentDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
            shipmentDateStr = shipmentDate;
          } else {
            // その他の形式の場合はformatDateを使用
            shipmentDateStr = formatDate(shipmentDate);
          }
        }
        
        if (shipmentDateStr) {
          // 同じ発送日・納入先の明細を取得
          // 注意: バックエンドのlimit上限は1000なので、複数回に分けて取得する必要がある
          let sameDestinationItems = [];
          try {
            let offset = 0;
            const limit = 1000;
            let hasMore = true;
            
            while (hasMore) {
              const batch = await shippingApi.getShipmentPlan(limit, offset, {
                shipment_date: shipmentDateStr,
                nonyusaki: nonyusaki,
              });
              
              if (batch && batch.length > 0) {
                sameDestinationItems = sameDestinationItems.concat(batch);
                offset += limit;
                hasMore = batch.length === limit;
              } else {
                hasMore = false;
              }
            }
          } catch (apiError) {
            throw apiError;
          }
          
          // 現在の明細を除いた、同じ発送日・納入先の他の明細の総重量を合計
          const currentItemId = `${item.hacchu_code}-${item.hacchu_changecnt}-${item.hacchu_datacode}`;
          const otherItemsWeight = sameDestinationItems
            .filter(otherItem => {
              const otherItemId = `${otherItem.hacchu_code}-${otherItem.hacchu_changecnt}-${otherItem.hacchu_datacode}`;
              const isDifferent = otherItemId !== currentItemId;
              
              // 発送日が一致するか確認（latest_shipment_dateまたはshipment_dateを確認）
              const otherShipmentDate = otherItem.latest_shipment_date || otherItem.shipment_date;
              // 発送日をYYYY-MM-DD形式に正規化して比較
              let otherShipmentDateStr = null;
              if (otherShipmentDate) {
                if (typeof otherShipmentDate === 'string' && otherShipmentDate.match(/^\d{4}-\d{2}-\d{2}$/)) {
                  otherShipmentDateStr = otherShipmentDate;
                } else if (otherShipmentDate instanceof Date) {
                  otherShipmentDateStr = otherShipmentDate.toISOString().split('T')[0];
                } else if (typeof otherShipmentDate === 'string') {
                  try {
                    const date = new Date(otherShipmentDate);
                    if (!isNaN(date.getTime())) {
                      otherShipmentDateStr = date.toISOString().split('T')[0];
                    }
                  } catch (e) {
                    // 日付のパースに失敗した場合はnullのまま
                  }
                }
              }
              const shipmentDateMatches = otherShipmentDateStr === shipmentDateStr;
              
              // 現在の明細と異なり、かつ発送日が一致する場合のみ含める
              return isDifferent && shipmentDateMatches;
            })
            .reduce((sum, otherItem) => {
              return sum + (otherItem.total_weight || 0);
            }, 0);
          
          // デフォルト重量 + 他の明細の重量
          const totalWeight = defaultWeight + otherItemsWeight;
          setShipmentDestinationTotalWeight(totalWeight);
        } else {
          // 発送日が変換できない場合はデフォルト重量のみ
          setShipmentDestinationTotalWeight(defaultWeight);
        }
      } catch (error) {
        console.error('発送先総重量取得エラー:', error);
        // エラー時はデフォルト重量のみ
        setShipmentDestinationTotalWeight(defaultWeight);
      } finally {
        setLoadingTotalWeight(false);
      }
    } else {
      // 発送日または納入先が設定されていない場合はデフォルト重量のみ
      setShipmentDestinationTotalWeight(defaultWeight);
    }
  };

  const applyShipmentPlanRowPatch = useCallback((patch, row) => {
    const rowKey = getRowId(row);
    setItems((prevItems) =>
      prevItems.map((item) => {
        if (getRowId(item) !== rowKey) return item;
        const updated = { ...item, ...patch };
        if (
          patch.shipment_date !== undefined &&
          (!item.split_plans || item.split_plans.length === 0)
        ) {
          updated.latest_shipment_date = patch.shipment_date;
        }
        return updated;
      })
    );
    setSelectedItem((prev) => {
      if (!prev || getRowId(prev) !== rowKey) return prev;
      const updated = { ...prev, ...patch };
      if (
        patch.shipment_date !== undefined &&
        (!prev.split_plans || prev.split_plans.length === 0)
      ) {
        updated.latest_shipment_date = patch.shipment_date;
      }
      return updated;
    });
  }, []);

  const shipmentPlanFields = useShipmentPlanFieldSave({
    formatDate: formatShipmentPlanDate,
    onPatchRow: applyShipmentPlanRowPatch,
    onAfterShipmentDateSaved: calculateShipmentDestinationTotalWeight,
  });

  const handleDetailClick = async (row, e) => {
    e.stopPropagation();
    setSelectedItem(row);
    shipmentPlanFields.initFromRow(row);
    setDrawerOpen(true);
    await calculateShipmentDestinationTotalWeight(row);
  };

  const handleSplitPlanDataChange = (rowId, splitPlans) => {
    setItems((prevItems) =>
      patchItemsSplitPlans(prevItems, rowId, getRowId, splitPlans, ['latest_shipment_date'])
    );
  };

  const handleDateChange = async (index, value) => {
    const item = items[index];
    if (!item) return;
    
    try {
      // バックエンドに保存
      await shippingApi.updateShipmentPlan(
        item.hacchu_code,
        item.hacchu_changecnt,
        item.hacchu_datacode,
        { shipment_date: value || null }
      );
      
      // ローカル状態を更新
      const updatedItems = [...items];
      updatedItems[index].shipment_date = value || null;
      // 分納計画の「発送日が確定していない行」（split_plans はあるが shipment_date がまだ1つもない）では
      // 一覧セルから入力した値をそのまま latest_shipment_date にも反映し、画面上の表示と保存内容を揃える。
      const hasSplitPlans = updatedItems[index].split_plans && updatedItems[index].split_plans.length > 0;
      const hasSplitPlanShipmentDate =
        hasSplitPlans && updatedItems[index].split_plans.some(plan => plan.shipment_date);
      if (!hasSplitPlanShipmentDate) {
        // 分納計画がない、または分納計画はあるがまだ発送日が入っていない場合は latest も更新する
        updatedItems[index].latest_shipment_date = value || null;
      }
      setItems(updatedItems);
      
      // 日付が入力された場合、次のレコードの入力欄にフォーカスを移し、カレンダーを開く
      if (value) {
        const nextIndex = index + 1;
        if (nextIndex < items.length) {
          const nextItem = items[nextIndex];
          const nextHasSplitPlans = nextItem.split_plans && nextItem.split_plans.length > 0;
          const nextHasSplitPlanShipmentDate =
            nextHasSplitPlans && nextItem.split_plans.some(plan => plan.shipment_date);
          // 「分納計画に発送日が入っている行」（セルが表示のみの行）の場合はそこで入力終了。
          // それ以外（分納無し、または分納ありだがまだ発送日未入力）は次の行に移動。
          if (!nextHasSplitPlanShipmentDate) {
            const nextKey = `shipment_date-${nextItem.hacchu_code}-${nextItem.hacchu_changecnt}-${nextItem.hacchu_datacode}`;
            // Reactの再レンダリングとrefの設定を待ってから次の行にフォーカス（行・既存値の有無で同じ挙動にするため遅延＋1回リトライ）
            const focusNextShipmentDateInput = () => {
              const nextInput = inputRefs.current[nextKey];
              if (!nextInput) return false;
              nextInput.focus();
              if (nextInput.showPicker && typeof nextInput.showPicker === 'function') {
                try {
                  nextInput.showPicker();
                } catch (err) {
                  nextInput.click();
                }
              } else {
                nextInput.click();
              }
              return true;
            };
            setTimeout(() => {
              if (!focusNextShipmentDateInput()) {
                setTimeout(() => focusNextShipmentDateInput(), 200);
              }
            }, 250);
          } else {
          }
        }
      }
    } catch (error) {
      console.error('発送日保存エラー:', error);
      alert('発送日の保存に失敗しました');
    }
  };

  const handleDateKeyDown = (e, index, field) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const nextIndex = index + 1;
      if (nextIndex < items.length) {
        const nextItem = items[nextIndex];
        const nextKey = `${field}-${nextItem.hacchu_code}-${nextItem.hacchu_changecnt}-${nextItem.hacchu_datacode}`;
        const focusNext = () => {
          const nextInput = inputRefs.current[nextKey];
          if (!nextInput) return false;
          nextInput.focus();
          if (nextInput.showPicker && typeof nextInput.showPicker === 'function') {
            try {
              nextInput.showPicker();
            } catch (err) {
              nextInput.click();
            }
          } else {
            nextInput.click();
          }
          return true;
        };
        setTimeout(() => {
          if (!focusNext()) setTimeout(focusNext, 100);
        }, 50);
      }
    }
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '';
    const date = new Date(dateStr);
    if (isNaN(date.getTime())) return '';
    return date.toISOString().split('T')[0];
  };

  const formatNumber = (num) => {
    if (num === null || num === undefined) return '';
    return num.toLocaleString('ja-JP');
  };

  const formatPrice = (price) => {
    if (price === null || price === undefined) return '';
    return price.toLocaleString('ja-JP', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  };

  const columns = [
    { 
      header: '操作', 
      field: 'actions',
      sticky: true,
      width: 80,
      render: (row) => (
        <button
          onClick={(e) => handleDetailClick(row, e)}
          className="detail-button px-3 py-1 bg-slate-600 text-white text-xs rounded hover:bg-slate-700 transition-colors print:hidden"
        >
          詳細
        </button>
      )
    },
    {
      header: '受注No/発注No',
      field: 'juchu_hacchu_no',
      render: (row) => {
        const hasSplitPlans = row.split_plans && row.split_plans.length > 0;
        const juchuNo = row.juchu_no || '-';
        const hacchuNo = row.hacchu_no || '-';
        return (
          <div
            className="text-slate-900 whitespace-normal break-words leading-snug"
            title={`${juchuNo}\n${hacchuNo}`}
            style={{ maxHeight: '3.2rem', overflow: 'hidden' }}
          >
            <div className="truncate">{juchuNo}</div>
            <div className="text-slate-600 truncate flex items-center gap-2">
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setSelectedHacchuNo(row.hacchu_no);
                  setPurchaseOrderModalOpen(true);
                }}
                className="text-blue-600 hover:text-blue-800 hover:underline cursor-pointer text-left print:hidden"
              >
                {hacchuNo}
              </button>
              <span className="hidden print:inline text-slate-900">{hacchuNo}</span>
              {hasSplitPlans && (
                <span
                  className="inline-flex items-center px-2 py-0.5 text-xs font-medium bg-blue-100 text-blue-700 rounded-full print:hidden"
                  title="分納あり"
                >
                  <svg className="w-3 h-3" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 6h16M4 12h16M4 18h16" />
                  </svg>
                </span>
              )}
            </div>
          </div>
        );
      },
    },
    {
      header: '得意先/納入先',
      field: 'customer_nonyusaki',
      render: (row) => {
        const customer = row.customer_name || '-';
        const destination = row.nonyusaki || '-';
        const fullText = `${customer}\n${destination}`;
        return (
          <div
            className="text-slate-900 whitespace-normal break-words leading-snug"
            title={fullText}
            style={{ maxHeight: '3.2rem', overflow: 'hidden' }} // 約2行分を表示
          >
            <div className="truncate">{customer}</div>
            <div className="text-slate-600 truncate">{destination}</div>
          </div>
        );
      },
    },
    { header: '製品CD', field: 'product_disp', render: (row) => row.product_disp || '-' },
    {
      header: '製品名/寸法',
      field: 'product_name_size',
      render: (row) => {
        const name = row.product_name || '-';
        const size = row.product_size || '-';
        const fullText = `${name}\n${size}`;
        return (
          <div
            className="text-slate-900 whitespace-normal break-words leading-snug"
            title={fullText}
            style={{ maxHeight: '3.2rem', overflow: 'hidden' }} // 約2行分を表示
          >
            <div className="truncate">{name}</div>
            <div className="text-slate-600 truncate">{size}</div>
          </div>
        );
      },
    },
    {
      header: '発送数量',
      field: 'hacchu_amount',
      render: (row) => {
        if (row.hacchu_amount == null) return '-';
        const amount = formatNumber(row.hacchu_amount);
        const unit = row.tani || '';
        return unit ? `${amount} ${unit}` : amount;
      }
    },
    { 
      header: '発送日', 
      field: 'latest_shipment_date',
      render: (row, index) => {
        const hasSplitPlans = row.split_plans && row.split_plans.length > 0;
        const hasSplitPlanShipmentDate = hasSplitPlans && 
          row.split_plans.some(plan => plan.shipment_date);
        
        const getDisplayDate = () => {
          if (hasSplitPlanShipmentDate) {
            const latestShipmentDate = row.split_plans.reduce((latest, plan) => {
              if (!plan.shipment_date) return latest;
              const planDate = new Date(plan.shipment_date);
              if (!latest || planDate > latest) return planDate;
              return latest;
            }, null);
            return latestShipmentDate ? latestShipmentDate.toISOString().split('T')[0] : (row.latest_shipment_date || row.shipment_date);
          }
          return row.latest_shipment_date || row.shipment_date;
        };
        const displayDateStr = formatDate(getDisplayDate()) || '-';
        
        if (hasSplitPlanShipmentDate) {
          return <span className="text-slate-900">{displayDateStr}</span>;
        }
        
        const displayDate = row.latest_shipment_date || row.shipment_date;
        const key = `shipment_date-${row.hacchu_code}-${row.hacchu_changecnt}-${row.hacchu_datacode}`;
        return (
          <>
            <span className="hidden print:inline text-slate-900">{displayDateStr}</span>
            <input
              ref={(el) => {
                if (el) {
                  inputRefs.current[key] = el;
                } else {
                  delete inputRefs.current[key];
                }
              }}
              type="date"
              value={formatDate(displayDate) || ''}
              onChange={(e) => {
                const newValue = e.target.value;
                const oldValue = formatDate(displayDate) || '';
                if (newValue !== oldValue) {
                  handleDateChange(index, newValue);
                }
              }}
              onKeyDown={(e) => handleDateKeyDown(e, index, 'shipment_date')}
              onClick={(e) => e.stopPropagation()}
              onFocus={(e) => e.stopPropagation()}
              onBlur={(e) => e.stopPropagation()}
              className="w-full px-2 py-1 text-sm border border-slate-300 rounded focus:outline-none focus:ring-2 focus:ring-slate-500 text-slate-900 print:hidden"
              style={{ minWidth: '120px' }}
            />
          </>
        );
      }
    },
    { header: '発注残数', field: 'hacchu_zan_amount', render: (row) => row.hacchu_zan_amount !== null && row.hacchu_zan_amount !== undefined ? formatNumber(row.hacchu_zan_amount) : '-' },
    {
      header: 'メモ',
      field: 'memo_flags',
      render: (row) => <ShipmentMemoBadges row={row} />,
    },
  ];

  // 一覧は常に列定義どおり全表示（visibleColumns は columns と同一のエイリアス）
  const visibleColumns = columns;

  // 印刷用：検索条件の要約テキスト
  const getPrintSearchSummary = () => {
    const parts = [];
    parts.push(`基準日=${appliedFilters.searchBaseDate === 'ship_date' ? '発送日' : '発注日'}`);
    if (appliedFilters.startDate) parts.push(`開始日=${appliedFilters.startDate}`);
    if (appliedFilters.endDate) parts.push(`終了日=${appliedFilters.endDate}`);
    if (appliedFilters.productQuery) parts.push(`製品・寸法=${appliedFilters.productQuery}`);
    if (appliedFilters.customerName) parts.push(`得意先=${appliedFilters.customerName}${appliedFilters.customerNameExact ? '(完全一致)' : ''}`);
    if (appliedFilters.currentPeriodOnly) parts.push('今期のみ');
    if (appliedFilters.previousPeriodsOnly) parts.push('前期以前');
    if (appliedFilters.shipmentDateNullOnly) parts.push('発送日未設定');
    if (appliedFilters.shipmentDateSetOnly) parts.push('発送日設定済');
    if (appliedFilters.hacchuDateFromToday) parts.push('発注日今日以降');
    if (appliedFilters.shipmentDateTodayOnly) parts.push('発送日本日');
    if (appliedFilters.shipmentDateFromToday) parts.push('発送日今日以降');
    if (appliedFilters.shipmentDateNextBusinessDay) parts.push('発送日翌営業日');
    if (appliedFilters.shipmentDateFromNextBusinessDay) parts.push('発送日翌営業日以降');
    if (appliedFilters.defectiveOnly) parts.push('不具合品');
    return parts.length ? `検索条件: ${parts.join(', ')}` : '検索条件: なし';
  };

  const columnLabels = {
    actions: '操作',
    juchu_no: '受注No',
    hacchu_no: '発注No',
    product_disp: '製品CD',
    product_name_size: '製品名/寸法',
    hacchu_amount: '発送数量',
    tani: '単位',
    hacchu_zan_amount: '発注残数',
    latest_shipment_date: '発送日',
    customer_nonyusaki: '得意先/納入先',
    arrival_date: '着日',
    nonyusaki_direct_address: '納入先名/納入先住所',
    pl_qty: 'PL数',
    package_qty: '個口数量',
    transport_service: '運送便',
    product_weight: '1個の重量',
    total_weight: '総重量',
    shipment_destination_total_weight: '発送先総重量',
    customer_code_for_print: '得意先CD',
    juchu_hacchu_no: '受注No/発注No',
    tracking_number: '送り状No',
    comment: 'コメント',
  };

  const selectedPrintFields = PRINT_PRESET_FIELDS.filter((field) => (
    printSelectedFields.includes(field) || MANDATORY_PRINT_FIELDS.includes(field)
  ));

  // 印刷時の推定ページ数を計算するための、1ページあたりの想定行数
  // 現状の環境では見出し込みで約17件/ページ表示されるため、その値を使用
  const PRINT_ROWS_PER_PAGE = 17;

  const renderPrintView = () => {
    const sourceItems = printItems.length ? printItems : items;
    const normalizeDate = (value) => {
      if (!value) return null;
      if (typeof value === 'string') {
        const trimmed = value.trim();
        if (!trimmed) return null;
        // ISO文字列や "YYYY-MM-DD" のどちらも日付部分に正規化
        return trimmed.slice(0, 10);
      }
      if (value instanceof Date && !Number.isNaN(value.getTime())) {
        return value.toISOString().slice(0, 10);
      }
      return null;
    };
    const addDaysToDate = (dateStr, days) => {
      const base = new Date(`${dateStr}T00:00:00`);
      if (Number.isNaN(base.getTime())) return null;
      base.setDate(base.getDate() + days);
      return base.toISOString().slice(0, 10);
    };
    const shouldFilterByShipmentDate =
      appliedFilters.searchBaseDate === 'ship_date' &&
      (!!appliedFilters.startDate || !!appliedFilters.endDate);
    const isInShipmentDateRange = (shipmentDateStr) => {
      if (!shouldFilterByShipmentDate) return true;
      if (!shipmentDateStr) return false;
      if (appliedFilters.startDate && shipmentDateStr < appliedFilters.startDate) return false;
      if (appliedFilters.endDate && shipmentDateStr > appliedFilters.endDate) return false;
      return true;
    };

    const flattenedRows = sourceItems.flatMap((item) => {
      const splitPlans = Array.isArray(item.split_plans) ? item.split_plans : [];
      const hasSplitPlans = splitPlans.length > 0;
      const hasUnsetSplitShipmentDate = hasSplitPlans && splitPlans.some((plan) => !normalizeDate(plan.shipment_date));
      const productWeightNum = Number(item.product_weight);
      const hasProductWeight = Number.isFinite(productWeightNum);
      const parentShipmentDate = normalizeDate(item.latest_shipment_date || item.shipment_date);

      // 分納が無い、または未設定分納が混在する場合は親明細1行にフォールバック
      if (!hasSplitPlans || hasUnsetSplitShipmentDate) {
        if (!isInShipmentDateRange(parentShipmentDate)) return [];
        return [{
          ...item,
          latest_shipment_date: parentShipmentDate,
          shipment_date: parentShipmentDate,
          total_weight: hasProductWeight && item.hacchu_amount != null
            ? productWeightNum * Number(item.hacchu_amount)
            : item.total_weight,
        }];
      }

      // 分納あり（全分納で発送日あり）の場合は、同一明細・同一発送日を合算して1行化
      const qtyByDate = splitPlans.reduce((acc, plan) => {
        const shipmentDate = normalizeDate(plan.shipment_date);
        if (!shipmentDate) return acc;
        const plannedQty = Number(plan.planned_qty);
        const current = acc.get(shipmentDate) || 0;
        acc.set(shipmentDate, current + (Number.isFinite(plannedQty) ? plannedQty : 0));
        return acc;
      }, new Map());

      return Array.from(qtyByDate.entries())
        .sort(([a], [b]) => a.localeCompare(b))
        .filter(([shipmentDate]) => isInShipmentDateRange(shipmentDate))
        .map(([shipmentDate, summedQty]) => {
          const leadTime = Number(item.arrival_lead_time);
          const arrivalDate = Number.isFinite(leadTime) && shipmentDate
            ? addDaysToDate(shipmentDate, leadTime)
            : null;
          return {
            ...item,
            latest_shipment_date: shipmentDate,
            shipment_date: shipmentDate,
            hacchu_amount: summedQty,
            total_weight: hasProductWeight ? productWeightNum * summedQty : null,
            arrival_date: arrivalDate || item.arrival_date || null,
          };
        });
    });

    const printCount = Math.min(flattenedRows.length, 1000);
    const total = printCount;
    const rowsForPrint = flattenedRows.slice(0, printCount);
    const shipmentDestinationTotalWeightMap = rowsForPrint.reduce((acc, row) => {
      const shipmentDate = row.latest_shipment_date || null;
      const nonyusaki = row.nonyusaki || null;
      if (!shipmentDate || !nonyusaki) return acc;
      const totalWeight = Number(row.total_weight);
      if (!Number.isFinite(totalWeight)) return acc;
      const key = `${shipmentDate}||${nonyusaki}`;
      acc[key] = (acc[key] || 0) + totalWeight;
      return acc;
    }, {});

    const getDisplayValue = (value) => {
      if (value === null || value === undefined || value === '') return '-';
      return value;
    };
    const formatWeightValue = (value) => {
      if (value === null || value === undefined || value === '') return '-';
      const numericValue = Number(value);
      if (!Number.isFinite(numericValue)) return '-';
      return numericValue.toLocaleString('ja-JP', { maximumFractionDigits: 3 });
    };

    const handleTogglePrintField = (field) => {
      if (MANDATORY_PRINT_FIELDS.includes(field)) return;
      setPrintSelectedFields((prev) => {
        const next = new Set(prev);
        if (next.has(field)) {
          next.delete(field);
        } else {
          next.add(field);
        }
        MANDATORY_PRINT_FIELDS.forEach((f) => next.add(f));
        return PRINT_PRESET_FIELDS.filter((f) => next.has(f));
      });
    };

    const handleSelectAllPrintFields = () => {
      setPrintSelectedFields([...PRINT_PRESET_FIELDS]);
    };

    const handleClearPrintFields = () => {
      setPrintSelectedFields([...MANDATORY_PRINT_FIELDS]);
    };

    return (
      <div className="h-full flex flex-col bg-white print-view-root">
        {/* 画面用ヘッダー（印刷時は非表示） */}
        <div className="sticky top-0 z-20 page-section print:hidden">
          <div className="page-header mb-2">
            <div>
              <h1 className="text-xl font-semibold text-slate-900 compact:text-lg">発送予定一覧（印刷用）</h1>
              <p className="meta-text mt-1">{getPrintSearchSummary()}</p>
              <p className="text-xs text-slate-500">
                ※ 1000件を超える印刷はできません。条件を絞り込んでください。
              </p>
            </div>
            <div className="page-header-actions">
              <button
                type="button"
                onClick={() => {
                  // 新しいタブで開かれている場合はタブを閉じるのが自然
                  if (window.opener && window.history.length <= 1) {
                    window.close();
                    return;
                  }
                  // それ以外の場合は、print_view パラメータを外して通常ビューに戻す
                  const url = new URL(window.location.href);
                  url.searchParams.delete('print_view');
                  window.location.href = url.toString();
                }}
                className="btn-toolbar text-sm border border-slate-300 rounded-md text-slate-700 bg-white hover:bg-slate-100"
              >
                戻る
              </button>
              <div className="meta-text">
                件数: {printCount ?? '-'}
              </div>
              <button
                type="button"
                onClick={() => {
                  const totalCount = printCount || 0;
                  if (totalCount > 1000) {
                    alert(`現在の検索条件では ${totalCount} 件が該当し、印刷上限の1000件を超えています。条件を絞り込んでから再度お試しください。`);
                    return;
                  }
                  if (totalCount > 200) {
                    const ok = window.confirm(
                      `現在の検索条件では ${totalCount} 件を印刷しようとしています。本当に印刷してよいですか？`
                    );
                    if (!ok) return;
                  }
                  window.print();
                }}
                className="btn-toolbar bg-slate-700 text-white rounded-md text-sm hover:bg-slate-800"
              >
                印刷実行
              </button>
            </div>
          </div>
          <div className="mt-3 p-3 border border-slate-200 rounded-md bg-slate-50">
            <div className="page-header gap-3 mb-2">
              <div className="flex items-center gap-2 flex-wrap min-w-0">
                <span className="text-sm font-medium text-slate-800">表示選択</span>
                <span className="text-xs text-slate-500">
                  * 必須列（発送日 / 納入先名・納入先住所）
                </span>
              </div>
              <div className="flex items-center gap-2 shrink-0">
                <button
                  type="button"
                  onClick={handleSelectAllPrintFields}
                  className="px-2.5 py-1 text-xs border border-slate-300 rounded-md bg-white text-slate-700 hover:bg-slate-100"
                >
                  全選択
                </button>
                <button
                  type="button"
                  onClick={handleClearPrintFields}
                  className="px-2.5 py-1 text-xs border border-slate-300 rounded-md bg-white text-slate-700 hover:bg-slate-100"
                >
                  全解除
                </button>
              </div>
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-x-3 gap-y-1.5">
              {PRINT_PRESET_FIELDS.map((field) => {
                const checked = selectedPrintFields.includes(field);
                const required = MANDATORY_PRINT_FIELDS.includes(field);
                return (
                  <label
                    key={field}
                    className={`flex items-center gap-2 text-sm ${required ? 'text-slate-800' : 'text-slate-700'}`}
                  >
                    <input
                      type="checkbox"
                      checked={checked}
                      disabled={required}
                      onChange={() => handleTogglePrintField(field)}
                      className="w-4 h-4 text-slate-700 border-slate-300 rounded focus:ring-slate-500 disabled:opacity-70"
                    />
                    <span>
                      {columnLabels[field] || field}
                      {required ? ' *' : ''}
                    </span>
                  </label>
                );
              })}
            </div>
          </div>
        </div>
        {/* 印刷時のみ表示されるタイトル（1ページ目の先頭に出る） */}
        <div className={PRINT_PAGE_TITLE_ROW}>
          <h1 className={PRINT_PAGE_TITLE_H1}>発送予定一覧</h1>
          <span className={PRINT_PAGE_TITLE_COUNT}>件数: {printCount ?? '-'}</span>
        </div>
        <div className={PRINT_SCROLL_CONTAINER}>
          <table className={PRINT_TABLE}>
            <colgroup>
              {selectedPrintFields.map((field) => (
                <col key={field} style={{ width: PRINT_COLUMN_WIDTHS[field] || 'auto' }} />
              ))}
            </colgroup>
            <thead className={PRINT_THEAD}>
              {/* 検索条件行（印刷時のみ表示）。thead 内に置くことで各ページ先頭に繰り返し表示される */}
              <tr className={PRINT_SEARCH_ROW_TR}>
                <th colSpan={selectedPrintFields.length} className={PRINT_SEARCH_ROW_TH}>
                  {getPrintSearchSummary()}
                </th>
              </tr>
              <tr>
                {selectedPrintFields.map((field) => (
                  <th
                    key={field}
                    className={PRINT_RIGHT_ALIGN_FIELDS.has(field) ? PRINT_TH_RIGHT : PRINT_TH_LEFT}
                  >
                    {columnLabels[field] || field}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {rowsForPrint.map((row, idx) => (
                <tr key={idx} className={PRINT_TR_BODY}>
                  {selectedPrintFields.map((field) => {
                    if (field === 'product_name_size') {
                      const name = getDisplayValue(row.product_name);
                      const size = getDisplayValue(row.product_size);
                      return (
                        <td key={field} className={PRINT_TD_COMPOSITE}>
                          <div>{name}</div>
                          <div className="text-slate-600">{size}</div>
                        </td>
                      );
                    }
                    if (field === 'nonyusaki_direct_address') {
                      const nonyusaki = getDisplayValue(row.nonyusaki);
                      const directAddress = getDisplayValue(row.direct_address);
                      return (
                        <td key={field} className={PRINT_TD_COMPOSITE}>
                          <div>{nonyusaki}</div>
                          <div className="text-slate-600">{directAddress}</div>
                        </td>
                      );
                    }
                    if (field === 'hacchu_amount') {
                      const amount = row.hacchu_amount != null ? formatNumber(row.hacchu_amount) : null;
                      const unit = row.tani || '';
                      const display = amount == null ? '-' : (unit ? `${amount} ${unit}` : amount);
                      return (
                        <td key={field} className={PRINT_TD_NUM}>
                          {display}
                        </td>
                      );
                    }
                    if (field === 'hacchu_zan_amount') {
                      return (
                        <td key={field} className={PRINT_TD_NUM}>
                          {row.hacchu_zan_amount !== null && row.hacchu_zan_amount !== undefined
                            ? formatNumber(row.hacchu_zan_amount)
                            : '-'}
                        </td>
                      );
                    }
                    if (field === 'latest_shipment_date') {
                      return (
                        <td key={field} className={PRINT_TD_LEFT}>
                          {formatDate(row.latest_shipment_date) || '-'}
                        </td>
                      );
                    }
                    if (field === 'arrival_date') {
                      return (
                        <td key={field} className={PRINT_TD_LEFT}>
                          {formatDate(row.arrival_date) || '-'}
                        </td>
                      );
                    }
                    if (field === 'pl_qty') {
                      return (
                        <td key={field} className={PRINT_TD_NUM}>
                          {row.pl_qty !== null && row.pl_qty !== undefined ? row.pl_qty : '-'}
                        </td>
                      );
                    }
                    if (field === 'package_qty') {
                      const pq = row.package_qty;
                      const display =
                        pq === null || pq === undefined || pq === ''
                          ? '-'
                          : typeof pq === 'number'
                            ? formatNumber(pq)
                            : String(pq);
                      return (
                        <td key={field} className={PRINT_TD_NUM}>
                          {display}
                        </td>
                      );
                    }
                    if (field === 'juchu_hacchu_no') {
                      const juchuNo = getDisplayValue(row.juchu_no);
                      const hacchuNo = getDisplayValue(row.hacchu_no);
                      return (
                        <td key={field} className={PRINT_TD_COMPOSITE}>
                          <div>{juchuNo}</div>
                          <div className="text-slate-600">{hacchuNo}</div>
                        </td>
                      );
                    }
                    if (field === 'customer_code_for_print') {
                      const customerCode = row.customer_disp || row.customer_code || '-';
                      return (
                        <td key={field} className={PRINT_TD_LEFT}>
                          {customerCode}
                        </td>
                      );
                    }
                    if (
                      field === 'product_weight' ||
                      field === 'total_weight'
                    ) {
                      return (
                        <td key={field} className={PRINT_TD_NUM}>
                          {formatWeightValue(row[field])}
                        </td>
                      );
                    }
                    if (field === 'shipment_destination_total_weight') {
                      const shipmentDate = row.latest_shipment_date || null;
                      const nonyusaki = row.nonyusaki || null;
                      const key = shipmentDate && nonyusaki ? `${shipmentDate}||${nonyusaki}` : null;
                      const totalWeight = key ? shipmentDestinationTotalWeightMap[key] : null;
                      return (
                        <td key={field} className={PRINT_TD_NUM}>
                          {formatWeightValue(totalWeight)}
                        </td>
                      );
                    }
                    return (
                      <td
                        key={field}
                        className={
                          PRINT_RIGHT_ALIGN_FIELDS.has(field) ? PRINT_TD_DEFAULT_NUM : PRINT_TD_DEFAULT_LEFT
                        }
                      >
                        {getDisplayValue(row[field])}
                      </td>
                    );
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    );
  };

  if (isPrintView) {
    return renderPrintView();
  }

  return (
    <div className="h-full flex flex-col bg-slate-50">
      <div className="page-section">
        {/* 画面用見出し（印刷時非表示） */}
        <div className="page-header print:hidden">
          <h1 className="page-title">発送予定</h1>
          <div className="page-header-actions">
            <PageSyncMeta groups={['orders']} />
            <button
              type="button"
              onClick={() => {
                const params = new URLSearchParams(searchParams);
                params.set('print_view', '1');
                const url = `${window.location.pathname}?${params.toString()}`;
                window.open(url, '_blank');
              }}
              className="flex items-center gap-2 btn-toolbar bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors font-medium text-sm border border-slate-300"
            >
              印刷用表示
            </button>
            <button
              type="button"
              onClick={handleRefresh}
              disabled={loading}
              className="flex items-center gap-2 btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm disabled:opacity-50 disabled:cursor-not-allowed"
            >
              <svg
                className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`}
                xmlns="http://www.w3.org/2000/svg"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              更新
            </button>
          </div>
        </div>
        {/* 印刷用見出し・検索条件（印刷時のみ表示） */}
        <div className="hidden print:block">
          <h1 className="text-xl font-semibold text-slate-900 mb-1">発送予定一覧</h1>
          <p className="text-sm text-slate-600">{getPrintSearchSummary()}</p>
        </div>
      </div>
      <div className="page-section print:hidden">
        <form onSubmit={handleFilterSubmit} className="space-y-4">
          {/* 検索条件（1行レイアウト） */}
          <div className="flex flex-wrap items-end gap-4">
            {/* 製品名・寸法検索 */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                製品名・寸法
              </label>
              <div className="flex gap-2">
                <input
                  type="text"
                  value={filters.productQuery}
                  onChange={(e) => handleFilterChange('productQuery', e.target.value)}
                  placeholder="製品コード / 製品名 / 寸法で検索"
                  className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-slate-900"
                />
                {filters.productQuery && (
                  <button
                    type="button"
                    onClick={() => handleFilterClear('productQuery')}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                    title="クリア"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* 得意先名 */}
            <div className="flex-1 min-w-[200px]">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                得意先名
              </label>
              <div className="flex gap-2 items-center">
                <input
                  type="text"
                  value={filters.customerName}
                  onChange={(e) => handleFilterChange('customerName', e.target.value)}
                  placeholder="得意先名で検索（スペース区切りで複数指定可）"
                  className="flex-1 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-slate-900"
                />
                {filters.customerName && (
                  <button
                    type="button"
                    onClick={() => handleFilterClear('customerName')}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                    title="クリア"
                  >
                    ×
                  </button>
                )}
                <label className="flex items-center gap-1.5 px-3 py-2 text-sm text-slate-700 cursor-pointer whitespace-nowrap">
                  <input
                    type="checkbox"
                    checked={filters.customerNameExact}
                    onChange={(e) => handleFilterChange('customerNameExact', e.target.checked)}
                    className="w-4 h-4 text-slate-600 border-slate-300 rounded focus:ring-slate-500"
                  />
                  <span>完全一致</span>
                </label>
              </div>
            </div>

            {/* 検索基準日（トグルボタン） */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                検索基準日
              </label>
              <div className="flex gap-2">
                <button
                  type="button"
                  onClick={() => handleFilterChange('searchBaseDate', 'order_date')}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.searchBaseDate === 'order_date'
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  発注日
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange('searchBaseDate', 'ship_date')}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.searchBaseDate === 'ship_date'
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  発送日
                </button>
              </div>
            </div>

            {/* 開始日 */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                開始日
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.startDate}
                  onChange={(e) => handleFilterChange('startDate', e.target.value)}
                  className="w-40 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-slate-900"
                />
                {filters.startDate && (
                  <button
                    type="button"
                    onClick={() => handleFilterClear('startDate')}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                    title="クリア"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>

            {/* 終了日 */}
            <div className="flex-shrink-0">
              <label className="block text-sm font-medium text-slate-700 mb-1">
                終了日
              </label>
              <div className="flex gap-2">
                <input
                  type="date"
                  value={filters.endDate}
                  onChange={(e) => handleFilterChange('endDate', e.target.value)}
                  className="w-40 px-3 py-2 text-sm border border-slate-300 rounded-md focus:outline-none focus:ring-2 focus:ring-slate-500 focus:border-slate-500 text-slate-900"
                />
                {filters.endDate && (
                  <button
                    type="button"
                    onClick={() => handleFilterClear('endDate')}
                    className="px-3 py-2 text-sm text-slate-600 hover:text-slate-800"
                    title="クリア"
                  >
                    ×
                  </button>
                )}
              </div>
            </div>
          </div>

          {/* フィルタボタン */}
          <div className="filter-toolbar justify-between">
            <div className="filter-toolbar">
              {/* 発注日のフィルタ */}
              <div className="filter-group">
                <span className="text-xs text-slate-500 w-16 flex-shrink-0">発注日:</span>
                <button
                  type="button"
                  onClick={() => handleFilterChange('hacchuDateFromToday', !filters.hacchuDateFromToday)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.hacchuDateFromToday
                      ? 'bg-green-600 text-white hover:bg-green-700'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="発注日が今日以降"
                >
                  今日以降
                </button>
              </div>
              
              {/* 期のフィルタ */}
              <div className="filter-group">
                <span className="text-xs text-slate-500 w-16 flex-shrink-0">期:</span>
                <button
                  type="button"
                  onClick={() => handleFilterChange('currentPeriodOnly', !filters.currentPeriodOnly)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.currentPeriodOnly
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title={`今期（${fiscalMeta.current_ki}期）のみ`}
                >
                  今期のみ
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange('previousPeriodsOnly', !filters.previousPeriodsOnly)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.previousPeriodsOnly
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="前期以前のデータを表示"
                >
                  前期以前
                </button>
              </div>

              {/* 発送日のフィルタ */}
              <div className="filter-group">
                <span className="text-xs text-slate-500 w-16 flex-shrink-0">発送日:</span>
                <button
                  type="button"
                  onClick={() => handleFilterChange('shipmentDateNullOnly', !filters.shipmentDateNullOnly)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.shipmentDateNullOnly
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                >
                  未設定
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange('shipmentDateSetOnly', !filters.shipmentDateSetOnly)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.shipmentDateSetOnly
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="発送日がいずれか設定されているデータのみ"
                >
                  設定済
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange('shipmentDateTodayOnly', !filters.shipmentDateTodayOnly)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.shipmentDateTodayOnly
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="発送日が今日のみ"
                >
                  今日
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange('shipmentDateFromToday', !filters.shipmentDateFromToday)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.shipmentDateFromToday
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="発送日が今日以降"
                >
                  今日以降
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange('shipmentDateNextBusinessDay', !filters.shipmentDateNextBusinessDay)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.shipmentDateNextBusinessDay
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="発送日が翌営業日"
                >
                  翌営業日
                </button>
                <button
                  type="button"
                  onClick={() => handleFilterChange('shipmentDateFromNextBusinessDay', !filters.shipmentDateFromNextBusinessDay)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm ${
                    filters.shipmentDateFromNextBusinessDay
                      ? 'bg-slate-700 text-white hover:bg-slate-800'
                      : 'bg-slate-100 text-slate-700 hover:bg-slate-200'
                  }`}
                  title="発送日が翌営業日以降"
                >
                  翌営業日以降
                </button>
              </div>

              {/* その他のフィルタ */}
              <div className="filter-group">
                <span className="text-xs text-slate-500 w-16 flex-shrink-0">その他:</span>
                <button
                  type="button"
                  onClick={() => handleFilterChange('defectiveOnly', !filters.defectiveOnly)}
                  className={`px-3 py-2 rounded-md transition-colors font-medium text-sm border ${
                    filters.defectiveOnly
                      ? 'bg-red-300 text-white border-red-300 hover:bg-red-400'
                      : 'bg-red-50 text-red-400 border-red-200 hover:bg-red-100'
                  }`}
                >
                  不具合品
                </button>
              </div>
            </div>
            <div className="flex gap-2 shrink-0 compact:w-full compact:justify-end">
              <button
                type="submit"
                className="btn-toolbar bg-slate-700 text-white rounded-md hover:bg-slate-800 transition-colors font-medium text-sm"
              >
                検索
              </button>
              <button
                type="button"
                onClick={handleAllFiltersClear}
                className="btn-toolbar bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors font-medium text-sm"
              >
                クリア
              </button>
            </div>
          </div>
        </form>
      </div>
      <div className="flex-1 overflow-hidden flex">
        <div className="flex-1 flex flex-col page-section-x pt-4 pb-2 min-w-0 min-h-0">
          <div className="mb-2 bg-white rounded-lg shadow-sm border border-slate-200 h-full flex flex-col">
                <div className="flex-1 overflow-auto">
                  <table className="divide-y divide-slate-200 print:text-sm" style={{ minWidth: 'max-content', width: '100%' }}>
                    <thead className="bg-slate-50">
                      <tr>
                        {visibleColumns.map((col, idx) => (
                          <th
                            key={idx}
                            className={`px-4 py-3 text-left text-xs font-semibold text-slate-700 uppercase tracking-wider bg-slate-50 ${
                              col.sticky 
                                ? 'sticky top-0 left-0 z-30 shadow-[2px_0_4px_rgba(0,0,0,0.1)]' 
                                : 'sticky top-0 z-10'
                            } ${col.field === 'actions' ? 'print:hidden' : ''}`}
                            style={col.width ? { width: `${col.width}px`, minWidth: `${col.width}px` } : {}}
                          >
                            {col.header}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-slate-100">
                      {items.length === 0 ? (
                        <tr>
                          <td colSpan={visibleColumns.length} className="px-4 py-8 text-center text-slate-500">
                            データがありません
                          </td>
                        </tr>
                      ) : (
                        items.map((row, rowIdx) => {
                          const rowId = getRowId(row);
                          const isExpanded = expandedRows.has(rowId);
                          const rowStage = getShipmentRowStage(row.compflag, row.w_check);
                          return (
                            <React.Fragment key={rowId}>
                              <tr
                                onClick={(e) => handleRowClick(row, e)}
                                className={`cursor-pointer transition-colors ${getShipmentRowClassName(rowStage)}`}
                              >
                                {visibleColumns.map((col, colIdx) => {
                                  const isWrapColumn =
                                    col.field === 'product_name_size' ||
                                    col.field === 'customer_nonyusaki' ||
                                    col.field === 'juchu_hacchu_no';
                                  // 2行構成の列は上揃え
                                  const isMultiLineStackColumn =
                                    col.field === 'juchu_hacchu_no' ||
                                    col.field === 'product_name_size' ||
                                    col.field === 'customer_nonyusaki';
                                  return (
                                  <td 
                                    key={colIdx} 
                                    className={`px-4 py-3 text-sm text-slate-900 ${
                                      isWrapColumn
                                        ? isMultiLineStackColumn
                                          ? 'whitespace-normal align-top'
                                          : 'whitespace-normal align-middle break-words'
                                        : 'whitespace-nowrap'
                                    } ${
                                      col.sticky 
                                        ? 'sticky left-0 z-10 shadow-[2px_0_4px_rgba(0,0,0,0.1)]' 
                                        : ''
                                    } ${
                                      col.sticky
                                        ? getShipmentRowStickyCellClassName(rowStage)
                                        : ''
                                    } ${
                                      col.field === 'latest_shipment_date' && isShipmentDateUnset(row)
                                        ? getShipmentDateUnsetCellClassName(true)
                                        : ''
                                    } ${col.field === 'actions' ? 'print:hidden' : ''}`}
                                    style={
                                      col.width
                                        ? { width: `${col.width}px`, minWidth: `${col.width}px` }
                                        : isWrapColumn
                                          ? { minWidth: '160px', maxWidth: '360px' }
                                          : {}
                                    }
                                  >
                                    {col.render ? col.render(row, rowIdx) : row[col.field]}
                                  </td>
                                  );
                                })}
                              </tr>
                              {isExpanded && (
                                <tr className="bg-slate-50">
                                  {/* 固定列のセル（操作列の位置） */}
                                  <td 
                                    className="sticky left-0 z-10 bg-slate-50 p-0 border-t border-slate-200 print:hidden"
                                    style={{ width: '80px', minWidth: '80px' }}
                                  >
                                    {/* 固定列部分は背景のみ、コンテンツは表示しない */}
                                  </td>
                                  {/* 残りの列にまたがるセル */}
                                  <td 
                                    colSpan={visibleColumns.length - 1} 
                                    className="p-0 bg-slate-50 border-t border-slate-200"
                                  >
                                    <HacchuSplitPlanInlineEditor
                                      hacchuCode={row.hacchu_code}
                                      hacchuChangecnt={row.hacchu_changecnt}
                                      hacchuDatacode={String(row.hacchu_datacode)}
                                      hacchuAmount={row.hacchu_amount}
                                      onDataChange={(splitPlans) => handleSplitPlanDataChange(rowId, splitPlans)}
                                      hideSupplierResponseDueDate={true}
                                      initialSplitPlans={row.split_plans ?? []}
                                    />
                                  </td>
                                </tr>
                              )}
                            </React.Fragment>
                          );
                        })
                      )}
                    </tbody>
                  </table>
                  {hasMore && items.length > 0 && (
                    <div ref={observerTarget} className="h-1 flex-shrink-0 bg-transparent" style={{ minHeight: '1px' }} />
                  )}
                </div>
              </div>
              {loading && items.length > 0 && (
                <div className="flex items-center justify-center py-3 bg-slate-50 border-t border-slate-200 rounded-b-lg flex-shrink-0">
                  <div className="flex items-center space-x-2 text-slate-600">
                    <svg className="animate-spin h-5 w-5 text-slate-500" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                      <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                      <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                    </svg>
                    <span className="text-sm font-medium">読み込み中...</span>
                  </div>
                </div>
              )}
              {!hasMore && items.length > 0 && !loading && (
                <div className="text-center py-3 text-slate-500 text-sm bg-slate-50 border-t border-slate-200 rounded-b-lg flex-shrink-0">すべてのデータを表示しました</div>
              )}
        </div>
      </div>
      <DetailDrawer
          isOpen={drawerOpen}
          onClose={async () => {
            await shipmentPlanFields.handleDrawerClose();
            setDrawerOpen(false);
          }}
          title={selectedItem ? `${selectedItem.hacchu_no} - ${selectedItem.product_name}` : '詳細'}
        >
          {selectedItem && (
            <div className="space-y-4">
              {/* 基本情報カード */}
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <h3 className="font-semibold text-lg mb-3 text-slate-900">基本情報</h3>
                <div className="grid grid-cols-2 gap-x-6 gap-y-2.5 text-sm">
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">仕入先CD</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.supplier_disp}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">仕入先名</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.supplier_name}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">資材担当</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_shain_name || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">営業担当</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.tanto_shain_name || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発注日</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_date || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">資材発注No</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_no}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発注訂正回数</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_changecnt ?? 0}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">製品コード</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.product_disp || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">製品名</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.product_name}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">寸法</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.product_size || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発注数量</span>
                    <span className="text-slate-900 flex-1 break-words">{formatNumber(selectedItem.hacchu_amount)} {selectedItem.tani || ''}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発注残数</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.hacchu_zan_amount !== null && selectedItem.hacchu_zan_amount !== undefined ? formatNumber(selectedItem.hacchu_zan_amount) : '-'} {selectedItem.tani || ''}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">仕入単価</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.stanka !== null && selectedItem.stanka !== undefined ? formatPrice(selectedItem.stanka) : '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">出荷形態</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.shukka_type || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">受注No</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.juchu_no || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">依頼No</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.hcirai_no || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">希望納期</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.nouki || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">得意先</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.customer_name || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">納入先名</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.nonyusaki || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">納入先住所</span>
                    <span className="text-slate-900 flex-1 break-words">{selectedItem.direct_address || '-'}</span>
                  </div>
                  <div className="flex items-start gap-3">
                    <span className="font-medium text-slate-600 w-32 flex-shrink-0 pt-0.5">発送日</span>
                    <span className="text-slate-900 flex-1 break-words">{formatDate(selectedItem.shipment_date) || '-'}</span>
                  </div>
                </div>
              </div>

              {/* 調整・発送情報カード */}
              <div className="bg-white rounded-lg border border-slate-200 p-4">
                <h3 className="font-semibold text-lg mb-3 text-slate-900">調整・発送情報</h3>
                <ShipmentPlanDetailFields
                  row={selectedItem}
                  draft={shipmentPlanFields.draft}
                  onDraftChange={shipmentPlanFields.updateDraftField}
                  onFieldBlur={shipmentPlanFields.saveFieldOnBlur}
                  loadingTotalWeight={loadingTotalWeight}
                  shipmentDestinationTotalWeight={shipmentDestinationTotalWeight}
                />
              </div>
            </div>
          )}
        </DetailDrawer>

      <PurchaseOrderModal
        isOpen={purchaseOrderModalOpen}
        onClose={() => {
          setPurchaseOrderModalOpen(false);
          setSelectedHacchuNo(null);
          // モーダルを閉じた際にデータを再取得
          loadInitialItems();
        }}
        onBulkApplied={loadInitialItems}
        hacchuNo={selectedHacchuNo}
      />

      <Modal
        isOpen={periodWarningOpen}
        onClose={() => setPeriodWarningOpen(false)}
        title="期フィルタと期間指定について"
        size="sm"
      >
        <div className="space-y-4">
          <p className="text-sm text-slate-700">
            「今期のみ」または「前期以前」がオンの状態で、期をまたぐ可能性のある期間が指定されています。
            このまま検索すると、指定した期間に含まれる一部の期のデータは表示されない場合があります。
          </p>
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              type="button"
              className="flex-1 px-4 py-2 bg-slate-700 text-white rounded-md hover:bg-slate-800 text-sm font-medium"
              onClick={() => {
                if (!pendingFiltersForPeriodWarning) {
                  setPeriodWarningOpen(false);
                  return;
                }
                setAppliedFilters(pendingFiltersForPeriodWarning);
                updateSearchParams(pendingFiltersForPeriodWarning);
                setPeriodWarningOpen(false);
              }}
            >
              <span className="whitespace-nowrap">期フィルタを維持して検索</span>
            </button>
            <button
              type="button"
              className="flex-1 px-4 py-2 bg-white text-slate-700 border border-slate-300 rounded-md hover:bg-slate-50 text-sm font-medium"
              onClick={() => {
                if (!pendingFiltersForPeriodWarning) {
                  setPeriodWarningOpen(false);
                  return;
                }
                const cleared = {
                  ...pendingFiltersForPeriodWarning,
                  currentPeriodOnly: false,
                  previousPeriodsOnly: false,
                };
                setFilters(cleared);
                setAppliedFilters(cleared);
                updateSearchParams(cleared);
                setPeriodWarningOpen(false);
              }}
            >
              <span className="whitespace-nowrap">期フィルタを外して検索</span>
            </button>
          </div>
        </div>
      </Modal>

      {loadingMore && (
        <div className="fixed bottom-6 left-1/2 transform -translate-x-1/2 z-50">
          <div className="flex items-center gap-2 px-4 py-2 bg-slate-700 text-white rounded-lg shadow-lg">
            <svg className="animate-spin h-4 w-4" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
              <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
              <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
            </svg>
            <span className="text-sm font-medium">追加データを読み込み中...</span>
          </div>
        </div>
      )}
    </div>
  );
};

export default ShippingAdjustPage;
